import { apiClient } from '../../../../core/apiClient';
import type {
  ClassificationResult,
  IngestionResult,
  ProcessingStats,
} from './types';

export const parserService = {
  /**
   * Classify a document to determine if it's compliance-related
   */
  async classifyDocument(file: File): Promise<ClassificationResult> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.getInstance().post('/parser/classify', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data.data;
  },

  /**
   * Ingest and process a document to create a framework template.
   *
   * Uses the new Framework Architect pipeline by default — specialist AI
   * agents (automotive, ISMS, quality, data protection, carbon, H&S,
   * financial, NHS) produce audit-ready templates with expert-level depth.
   *
   * Falls back to the legacy parser if the Architect endpoint is
   * unavailable.
   */
  async ingestDocument(file: File): Promise<IngestionResult> {
    const formData = new FormData();
    formData.append('file', file);

    // Architect pipeline. Large statutory instruments (ESOS-scale, ~100
    // regulations) can take 20-30 min when Anthropic is rate-limiting or
    // clause_ledger_expert produces 30K+ output tokens, so give it 45 min.
    // If it fails for a non-timeout reason we still fall back to legacy so
    // unavailable/5xx doesn't dead-end the user, but a client timeout is
    // NEVER recovered by a second full parse on the same document — that
    // just races the first parse and causes a duplicate-key error in
    // framework_versions.
    try {
      const response = await apiClient.getInstance().post('/parser/architect', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 45 * 60 * 1000,
      });
      const r = response.data;
      if (r?.success && r?.framework_id && r?.template_id) {
        // Reshape to match the legacy IngestionResult contract
        return {
          framework: { id: r.framework_id, name: r.framework_name || 'AI-Architected Framework' },
          version: { id: 0, version: '1.0' },
          template: { id: r.template_id, name: r.framework_name || 'AI-Architected Template' },
          nodesCreated: r.stats?.requirements ?? 0,
          fieldsCreated: r.stats?.fields ?? 0,
        } as IngestionResult;
      }
      throw new Error(r?.message || 'Architect returned no framework');
    } catch (err: any) {
      // PARSE_IN_PROGRESS (HTTP 409) — another parse is already running for
      // this user. DO NOT fall back, DO NOT retry, DO NOT start a second
      // parse. Surface the server's message verbatim so the user knows to
      // wait rather than reload/retry (which is what caused the duplicate-
      // parse cost incident).
      const code = err?.response?.data?.code || err?.response?.data?.error;
      if (err?.response?.status === 409 || code === 'PARSE_IN_PROGRESS') {
        throw new Error(err?.response?.data?.message || 'A parse is already running on your account. Wait for it to complete before starting another.');
      }
      // Client timeout or network abort → DO NOT fall back. The backend is
      // probably still running the same parse; starting a second one would
      // hit the unique-key on framework_versions. Surface the error so the
      // user can check /admin/document-library in a minute to see whether
      // the parse eventually landed.
      const isTimeout = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '');
      if (isTimeout) {
        throw new Error('Parser is taking longer than expected. Give it a few more minutes and check Document Library — the template usually lands even when the request appears to time out.');
      }
      // Non-timeout failure (architect endpoint itself errored) → fall back to legacy
      console.warn('Architect pipeline failed, falling back to legacy parser:', err?.message);
      const fallback = await apiClient.getInstance().post('/parser/ingest', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 45 * 60 * 1000,
      });
      return fallback.data.data;
    }
  },

  /**
   * Generate a compliance framework from a description
   */
  async generateAIFramework(description: string, standardName: string) {
    const response = await apiClient.getInstance().post('/parser/ai/generate-standard', {
      description,
      standardName,
    });

    return response.data.data;
  },

  /**
   * Fetch parser statistics
   */
  async getStats(): Promise<ProcessingStats> {
    const response = await apiClient.getInstance().get('/parser/stats');
    return response.data.data;
  },

  /**
   * Poll for parser progress events
   */
  async getProgress(since: number = 0) {
    const response = await fetch(`/api/parser/progress?since=${since}`);
    const data = await response.json();
    return data.success ? data.data : null;
  },
};

/**
 * Normalize error messages from API responses
 */
export const normalizeError = (err: any): string => {
  if (!err) return 'Unknown error';
  if (typeof err === 'string') return err;
  const respData = err?.response?.data;
  const message = respData?.error || respData?.message || err.message;
  if (message) return String(message);
  try {
    return JSON.stringify(err);
  } catch {
    return 'Unknown error';
  }
};

/**
 * Get user-friendly error messages for specific error scenarios
 */
export const getParserErrorMessage = (err: any): string => {
  if (err?.response?.status === 404) {
    return 'Parser endpoint unavailable. Please restart backend with /api/parser routes.';
  }

  const backendMessage: string | undefined = err?.response?.data?.error;
  if (backendMessage && backendMessage.toLowerCase().includes('compliance page')) {
    return 'Only one compliance page is allowed per branch. Please disable the existing compliance page before enabling another, then retry.';
  }

  return normalizeError(err);
};
